import { Typography } from '@euler/app/components/typography';
import { TaskListItem } from '@euler/model/TaskListItem';
import { CommonTaskStatus } from '@euler/model/enum';
import { dateFromValue, formatDuration } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import { memo } from 'react';
import { View } from 'react-native';
import { maskCustomerName } from '../../functions/maskCustomerName';
import { useBoardUnit } from '../../functions/useBoardUnit';
import { workshopBoardColors as colors } from '../../workshopBoardColors';
import { kBoardRowHeight } from '../board-table/boardColumns';
import { StageCell } from './StageCell';
import { kTaskColumns } from './columns';

/** One open task: its car, who it is for, where it is, and for how long. */
export const TaskRow = memo(
  ({ task, now }: { task: TaskListItem; now: Date }) => {
    const u = useBoardUnit();
    const createdAt = dateFromValue(task.orderedAt);
    const customer = maskCustomerName(task.customerName);
    return (
      <View
        style={{
          height: u(kBoardRowHeight),
          flexDirection: 'row',
          alignItems: 'center',
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
        }}
      >
        <Typography.Text
          size={u(34)}
          bold
          color={colors.foreground}
          numberOfLines={1}
          style={{ flex: kTaskColumns.plate.flex }}
        >
          {task.licensePlateNo}
        </Typography.Text>
        <Typography.Text
          size={u(26)}
          color={customer ? colors.foreground : colors.faint}
          numberOfLines={1}
          style={{ flex: kTaskColumns.customer.flex }}
        >
          {customer ?? '—'}
        </Typography.Text>
        <StageCell
          kind="preInspection"
          status={task.preInspectionStatus}
          flex={kTaskColumns.preInspection.flex}
        />
        <StageCell
          kind="inspection"
          status={task.inspectionStatus}
          flex={kTaskColumns.inspection.flex}
        />
        <StageCell
          kind="construction"
          // Counted as 施工中 once a job is scheduled, as the status badge.
          status={
            task.isConstructionJobScheduled &&
            task.constructionStatus !== CommonTaskStatus.Finished
              ? CommonTaskStatus.InProgress
              : task.constructionStatus
          }
          flex={kTaskColumns.construction.flex}
        />
        <Typography.Text
          size={u(26)}
          color={colors.muted}
          numberOfLines={1}
          style={{
            flex: kTaskColumns.createdAt.flex,
            fontVariant: ['tabular-nums'],
          }}
        >
          {formatDateTime(createdAt, 'MM-dd HH:mm')}
        </Typography.Text>
        <Typography.Text
          size={u(26)}
          medium
          color={colors.foreground}
          numberOfLines={1}
          style={{
            flex: kTaskColumns.elapsed.flex,
            fontVariant: ['tabular-nums'],
          }}
        >
          {formatDuration(Math.max(now.getTime() - createdAt.getTime(), 0))}
        </Typography.Text>
      </View>
    );
  },
);
